<template>
  <div class="account-billing pt-3">
    <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

    <p class="lead-line">
      <strong>{{ accountName }}</strong> の請求と入金です。
      <span v-if="balanceTotal > 0" class="owing">
        未収 {{ yen(balanceTotal) }}（{{ worstStageLabel }}）
      </span>
      <span v-else class="settled">未収はありません。</span>
    </p>

    <div class="desktop-only responsive-table-shell">
      <table class="history-table">
        <thead>
          <tr>
            <th>対象月</th>
            <th class="numeric">請求額</th>
            <th class="numeric">入金</th>
            <th class="numeric">未収</th>
            <th>支払期限</th>
            <th>いまの状態</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in months" :key="row.yearMonth">
            <td>{{ readableMonth(row.yearMonth) }}</td>
            <td class="numeric">{{ yen(row.billedAmount) }}</td>
            <td class="numeric">{{ yen(row.paidAmount) }}</td>
            <td class="numeric">
              <span :class="{ owing: Number(row.balanceAmount) > 0 }">
                {{ yen(row.balanceAmount) }}
              </span>
            </td>
            <td>{{ row.paymentDueAt || '—' }}</td>
            <td>
              <CBadge :color="stageColor(row.stage)">
                {{ row.stageLabel }}
              </CBadge>
              <div v-if="row.overdueDays" class="overdue">
                {{ row.overdueDays }}日超過
              </div>
              <div v-if="row.notIssued" class="overdue">請求書が未発行です</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!--
      **スマホでは表を横に流さない。**6列を横スクロールで読ませると、
      月と金額が同時に見えなくなる。1件ずつの札にする。
    -->
    <div class="mobile-only mobile-record-list" aria-label="請求と入金">
      <article
        v-for="row in months"
        :key="row.yearMonth"
        class="mobile-record-card"
      >
        <div class="mobile-record-card__header">
          <strong>{{ readableMonth(row.yearMonth) }}</strong>
          <CBadge :color="stageColor(row.stage)">{{ row.stageLabel }}</CBadge>
        </div>
        <dl class="mobile-detail-list">
          <div><dt>請求額</dt><dd>{{ yen(row.billedAmount) }}</dd></div>
          <div><dt>入金</dt><dd>{{ yen(row.paidAmount) }}</dd></div>
          <div>
            <dt>未収</dt>
            <dd :class="{ owing: Number(row.balanceAmount) > 0 }">
              {{ yen(row.balanceAmount) }}
            </dd>
          </div>
          <div><dt>支払期限</dt><dd>{{ row.paymentDueAt || '—' }}</dd></div>
        </dl>
      </article>
      <div v-if="!months.length" class="mobile-empty-state">
        確定した請求がありません。
      </div>
    </div>

    <p v-if="!loading && !months.length" class="empty desktop-only">
      この会社の確定した請求がまだありません。
      <strong>確定は毎月1日に自動で走ります。</strong>
    </p>

    <p class="footnote">
      入金の記録と請求書の発行は
      <router-link to="/platform/billing">請求管理</router-link>
      で行います。全社をまとめて見るときは
      <router-link to="/platform/receivables">入金・売掛管理</router-link>
      を開いてください。
    </p>
  </div>
</template>

<script>
/**
 * その会社の請求と入金（契約企業一覧の中で見る）。
 *
 * ## なぜ作ったか
 *
 * 契約企業を見ていて「この会社、払ってる？」と思っても、
 * **そこから支払い状況へ辿る道がどこにも無かった。**請求管理を
 * 開いて、月を切り替えて、会社を目で探すしかない
 * （2026-09-18、利用者の指摘）。
 *
 * ## どこから見えるか
 *
 * **運営メニュー → 契約企業一覧 → 会社の［管理］→「請求・入金」タブ。**
 *
 * ## 読むだけ
 *
 * ここでは何も変わらない。入金の記録も請求書の発行も請求管理で行う。
 * **同じことを2か所からできるようにしない。**どちらで操作したか
 * 分からなくなる。
 */
import providerBilling from '@/services/providerBilling'

const STAGE_COLOR = {
  WITHIN_DUE: 'secondary',
  IN_GRACE: 'warning',
  READ_ONLY: 'danger',
  SUSPENDED: 'dark'
}

export default {
  name: 'AccountBillingHistory',

  props: {
    accountId: { type: [Number, String], required: true },
    accountName: { type: String, default: '' }
  },

  data () {
    return {
      loading: false,
      errorMessage: '',
      months: [],
      balanceTotal: 0,
      worstStageLabel: ''
    }
  },

  watch: {
    /*
     * **会社を選び直したら読み直す。**
     * タブは残ったまま中身の会社だけ変わるので、ここを見ていないと
     * 前の会社の請求を、別の会社の名前で見せてしまう。
     */
    accountId: {
      immediate: true,
      handler () {
        this.load()
      }
    }
  },

  methods: {
    load () {
      if (!this.accountId) return Promise.resolve()
      this.loading = true
      this.errorMessage = ''
      /*
       * **24か月ぶん見る。**契約企業の画面から見るときは、
       * 「いつから払えていないか」を遡ることが多い。
       */
      return providerBilling
        .receivables(24, this.accountId)
        .then(data => {
          const account = ((data && data.accounts) || [])[0]
          if (!account) {
            this.months = []
            this.balanceTotal = 0
            this.worstStageLabel = ''
            return
          }
          /* 新しい月が上。契約企業の画面では直近から見たい。 */
          this.months = Object.keys(account.months || {})
            .sort()
            .reverse()
            .map(key => account.months[key])
          this.balanceTotal = Number(account.balanceTotal || 0)
          this.worstStageLabel = account.worstStageLabel || ''
        })
        .catch(error => {
          this.errorMessage = this.readError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    stageColor (stage) {
      return STAGE_COLOR[stage] || 'secondary'
    },

    readableMonth (yearMonth) {
      const text = String(yearMonth || '')
      if (text.length !== 6) return text
      return text.slice(0, 4) + '年' + Number(text.slice(4, 6)) + '月'
    },

    yen (value) {
      return '¥' + Number(value || 0).toLocaleString('ja-JP')
    },

    readError (error) {
      const data = error && error.response && error.response.data
      if (data && data.message) return data.message
      return '請求と入金を読み込めませんでした。時間をおいて画面を再読み込みしてください。'
    }
  }
}
</script>

<style scoped>
/*
  **表とカードの出し分け。**

  desktop-only / mobile-only を書いただけでは効かない。この部品には
  その定義が無く、広い画面でも狭い画面でも両方が出ていた。

  一覧のクラス（mobile-record-list）にも display があり、同じ強さなら
  後ろに書いたほうが勝つ。**組み合わせで明示して、書き順に左右され
  ないようにする**（契約企業一覧と同じ書き方）。
*/
.mobile-only { display: none; }
.mobile-only.mobile-record-list { display: none; }
.mobile-record-list { display: grid; gap: 12px; }
.mobile-record-card {
  min-width: 0;
  padding: 14px;
  border: 1px solid #d8e4ec;
  border-radius: 12px;
  background: #fff;
}
.mobile-record-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}
.mobile-detail-list { display: grid; gap: 8px; margin: 0; }
.mobile-detail-list > div {
  display: grid;
  grid-template-columns: minmax(90px, 40%) minmax(0, 1fr);
  gap: 10px;
  min-width: 0;
}
.mobile-detail-list dt {
  margin: 0;
  color: #657987;
  font-size: 12px;
  font-weight: 700;
}
.mobile-detail-list dd {
  min-width: 0;
  margin: 0;
  text-align: right;
  overflow-wrap: anywhere;
}
.mobile-empty-state {
  padding: 24px 14px;
  border: 1px dashed #cbd8e1;
  border-radius: 12px;
  color: #687d8a;
  text-align: center;
}

.lead-line { margin-bottom: 0.8rem; }
.owing { color: #b3413d; font-weight: 600; }
.settled { color: #2e7d55; }

.history-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.history-table th,
.history-table td {
  border: 1px solid #e3e7eb;
  padding: 0.45rem 0.6rem;
  vertical-align: top;
}
.history-table thead th { background: #f4f6f8; white-space: nowrap; }
.numeric { text-align: right; }
.overdue { font-size: .75rem; color: #b3413d; margin-top: 0.15rem; }

.empty { color: #6b7783; margin-top: 0.8rem; }
.footnote { font-size: 0.82rem; color: #6b7783; margin-top: 1rem; }
@media (max-width: 767.98px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .mobile-only.mobile-record-list { display: grid; }
}
</style>
